import cx from "classnames";
import { FunctionComponent, useContext } from "react";
import { ActionsContext, LocalGameActions } from "./ActionsContext";
import { Color, MovementDirection, Player } from "./Types";
import { useAppSelector } from "./store/hooks";
import { pipCount } from "./store/gameBoardSlice";
import DoublingCube from "./DoublingCube";
import { GameState } from "./store/gameStateSlice";
import { getRankLabel } from "./services/FirebaseUsers";

export enum PlayerCardSide {
  Top = "TOP",
  Bottom = "BOTTOM",
}

type PlayerCardProps = {
  side: PlayerCardSide;
  playerPerspective: Player;
};

const PlayerCard: FunctionComponent<PlayerCardProps> = ({
  side,
  playerPerspective,
}: PlayerCardProps) => {
  const [
    gameBoardState,
    settings,
    currentPlayer,
    doublingCubeData,
    gameState,
    matchScore,
    players,
    myProfile,
  ] = useAppSelector((state) => [
    state.gameBoard,
    state.settings,
    state.currentPlayer,
    state.doublingCube,
    state.gameState,
    state.matchScore,
    state.players,
    state.user.profile,
  ]);
  const actions = useContext(ActionsContext);

  const playerOneColor = settings.playerOneColor;
  const playerTwoColor =
    playerOneColor === Color.White ? Color.Black : Color.White;

  const isLocalGame = actions instanceof LocalGameActions;

  // Определяем, какой игрок на какой стороне
  const isBottomMyPlayer = side === PlayerCardSide.Bottom;
  const player = isBottomMyPlayer ? playerPerspective : (playerPerspective === Player.One ? Player.Two : Player.One);

  // Никнейм
  let nickname = "";
  if (isLocalGame) {
    nickname = player === Player.One ? "Игрок 1" : "Игрок 2";
  } else {
    if (isBottomMyPlayer) {
      nickname = myProfile?.nickname ?? "Вы";
    } else {
      // Соперник
      const opponentKey = playerPerspective === Player.One ? "playerTwo" : "playerOne";
      nickname = players[opponentKey]?.nickname ?? "Соперник";
    }
  }

  // Рейтинг
  let rating: number | null = null;
  if (!isLocalGame) {
    if (isBottomMyPlayer) {
      rating = myProfile?.rating ?? null;
    } else {
      const opponentKey = playerPerspective === Player.One ? "playerTwo" : "playerOne";
      rating = players[opponentKey]?.rating ?? null;
    }
  }

  const pips = pipCount(gameBoardState, player);
  const color = player === Player.One ? playerOneColor : playerTwoColor;
  const playerScore = matchScore[player];

  let doublingCube = null;
  if (isBottomMyPlayer && doublingCubeData.owner === playerPerspective) {
    doublingCube = <DoublingCube />;
  } else if (!isBottomMyPlayer && doublingCubeData.owner !== null && doublingCubeData.owner !== playerPerspective) {
    doublingCube = <DoublingCube />;
  }

  const isCurrentTurn =
    (gameState === GameState.PlayerMoving ||
      gameState === GameState.PlayerOfferingDouble ||
      gameState === GameState.PlayerRolling) &&
    ((isBottomMyPlayer && currentPlayer === playerPerspective) ||
      (!isBottomMyPlayer && currentPlayer !== playerPerspective));

  return (
    <div
      className={cx("Player-card-wrapper", {
        bottom: isBottomMyPlayer,
        cw: settings.movementDirection === MovementDirection.Clockwise,
        current: isCurrentTurn,
      })}
    >
      <div className="Player-card-checker-wrapper">
        <div
          className={cx("Player-card-color-dot", {
            "dot-white": color === Color.White,
            "dot-black": color === Color.Black,
          })}
        />
      </div>
      <div className="Player-name-and-score-wrapper">
        <div className="Player-name-wrapper">{nickname}</div>
        {rating !== null && (
          <div className="Player-rating-wrapper">
            {rating} · {getRankLabel(rating)}
          </div>
        )}
        <div className="Player-score-wrapper">
          <div className="Player-pip-count-wrapper">{"п: " + pips}</div>
          <div className="Player-points-wrapper">
            {playerScore + "/" + matchScore.pointsRequiredToWin}
          </div>
        </div>
      </div>
      <div className="Player-card-doubling-cube-wrapper">{doublingCube}</div>
    </div>
  );
};

export default PlayerCard;
